if (!customElements.get("shipping-estimator")) {
  class ShippingEstimator extends HTMLElement {
    constructor() {
      super();
      // Default zip/postal codes for countries using the correct country value attributes
      this.defaultZipCodes = {
        "France": "75000", // Paris
        "Belgium": "1000", // Brussels
        "Luxembourg": "2001", // Luxembourg City
        "Germany": "10115", // Berlin
        "Austria": "1010", // Vienna
        "Bolivia": "0000", // La Paz
        "Spain": "28001", // Madrid
        "Italy": "00100", // Rome
        "Netherlands": "1012", // Amsterdam
        "Poland": "00-001", // Warsaw
        "Portugal": "1000-001", // Lisbon
        "United Kingdom": "SW1A 1AA", // London
        "Czech Republic": "110 00" // Prague
      };
      
      // Default provinces for countries that have provinces
      this.defaultProvinces = {
        "Spain": "Madrid",
        "Italy": "Roma", // Rome
        "Portugal": "Lisboa", // Lisbon
        "United Kingdom": "England"
      };

      // Initialize translations as empty - will be populated later
      this.translations = {};
    }
    
    // Method to get translations with fallbacks
    getTranslations() {
      // Try multiple methods to get the translations
      
      // Method 1: Read from data attributes
      const translations = {
        freeShippingMessage: this.dataset.freeShippingText || 
                            this.getAttribute('data-free-shipping-text') || 
                            'Free shipping from €75 purchase',
        pickupPointTitle: this.dataset.pickupTitle || 
                         this.getAttribute('data-pickup-title') || 
                         'Pickup point',
        pickupPointSubtitle: this.dataset.pickupSubtitle || 
                            this.getAttribute('data-pickup-subtitle') || 
                            'Near you',
        homeDeliveryTitle: this.dataset.homeDeliveryTitle || 
                          this.getAttribute('data-home-delivery-title') || 
                          'Home delivery',
        homeDeliverySubtitle: this.dataset.homeDeliverySubtitle || 
                             this.getAttribute('data-home-delivery-subtitle') || 
                             'Or at your workplace',
        deliveredOn: this.dataset.deliveredOn || 
                    this.getAttribute('data-delivered-on') || 
                    'Delivered on',
        noShippingOptions: this.dataset.noShipping || 
                          this.getAttribute('data-no-shipping') || 
                          'No shipping options available',
        mondialRelayName: this.dataset.mondialRelayName || 
                         this.getAttribute('data-mondial-relay-name') || 
                         'Mondial Relay',
        colissimoName: this.dataset.colissimoName || 
                      this.getAttribute('data-colissimo-name') || 
                      'Colissimo'
      };
      
      // Method 2: Check if translations are stored in a global variable (common Shopify pattern)
      if (typeof window.shippingEstimatorTranslations !== 'undefined') {
        Object.assign(translations, window.shippingEstimatorTranslations);
      }
      
      return translations;
    }
    
    connectedCallback() {
      // Get translations when element is connected
      this.translations = this.getTranslations();
      
      // Debug: Log translations to verify they're loaded
      console.log('Shipping Estimator Translations:', this.translations);
      
      this.setupCountries();
      this.submitButton = this.querySelector('[type="button"]');
      this.submitButton.addEventListener("click", this.estimateShipping.bind(this));
      this.results = this.querySelector(".shipping-estimator--results");
        
      // Hide zip code field if it exists
      const zipField = this.querySelector(".shipping-estimator-zip");
      if (zipField) {
        const zipFieldContainer = zipField.closest('.form-field') || zipField.parentElement;
        if (zipFieldContainer) {
          zipFieldContainer.style.display = 'none';
        }
      }
      
      // Add event listener for country change to auto-select default province
      const countrySelect = this.querySelector(".shipping-estimator-country");
      if (countrySelect) {
        countrySelect.addEventListener("change", this.handleCountryChange.bind(this));
      }
      
      // Auto-load shipping rates for France when the component is connected
      setTimeout(() => {
        // Make sure France is selected
        if (countrySelect && countrySelect.value !== "France") {
          countrySelect.value = "France";
          // Trigger change event to update any dependent fields
          countrySelect.dispatchEvent(new Event('change'));
        }
        // Calculate shipping rates for France
        this.estimateShipping();
      }, 500); // Small delay to ensure the component is fully initialized
    }
    
    handleCountryChange(event) {
      const country = event.target.value;
      const provinceSelect = this.querySelector(".shipping-estimator-province");
      
      // If the country has a default province and the province select exists
      if (this.defaultProvinces[country] && provinceSelect) {
        // Wait a moment for Shopify's province list to populate
        setTimeout(() => {
          // Find the option that matches or contains our default province
          const defaultProvince = this.defaultProvinces[country];
          const options = Array.from(provinceSelect.options);
          
          for (let i = 0; i < options.length; i++) {
            if (options[i].value === defaultProvince || options[i].text.includes(defaultProvince)) {
              provinceSelect.selectedIndex = i;
              break;
            }
          }
        }, 300);
      }
    }
    
    async estimateShipping() {
      // Ensure translations are loaded
      if (Object.keys(this.translations).length === 0) {
        this.translations = this.getTranslations();
      }
      
      const countrySelect = this.querySelector(".shipping-estimator-country");
      const provinceSelect = this.querySelector(".shipping-estimator-province");
      
      const country = countrySelect.value;
      let province = "";
      
      // Get province if available, otherwise use default
      if (provinceSelect && provinceSelect.value) {
        province = provinceSelect.value;
      } else if (this.defaultProvinces[country]) {
        province = this.defaultProvinces[country];
      }
      
      // Get default zip code for selected country
      const zip = this.getDefaultZipCode(country);
      
      this.submitButton.classList.add("loading");
      
      // First check if cart has items
      try {
        // Try to get cart.json
        const cartResponse = await fetch(`${window.theme.routes.cart_url}.js`);
        
        if (cartResponse.ok) {
          const cartData = await cartResponse.json();
          
          // If cart has items, try to get shipping rates from API
          if (cartData && cartData.items && cartData.items.length > 0) {
            // Cart has items - try to fetch real shipping rates
            try {
              const response = await fetch(
                `${window.theme.routes.cart_url}/prepare_shipping_rates.json?shipping_address[zip]=${zip}&shipping_address[country]=${country}&shipping_address[province]=${province}`,
                { method: "POST" }
              );
              
              if (response.ok) {
                try {
                  const shippingRates = await this.getShippingRates(
                    zip,
                    country,
                    province
                  );
                  this.formatShippingRates(shippingRates);
                } catch (error) {
                  console.log("Error fetching shipping rates, showing default rates:", error);
                  this.showDefaultShippingRates(country);
                }
              } else {
                console.log("Error in prepare_shipping_rates, showing default rates");
                this.showDefaultShippingRates(country);
              }
            } catch (error) {
              console.log("Network error, showing default rates:", error);
              this.showDefaultShippingRates(country);
            }
          } else {
            // Cart is empty - show default rates
            console.log("Cart is empty, showing default rates");
            this.showDefaultShippingRates(country);
          }
        } else {
          // Failed to get cart data - show default rates
          console.log("Failed to get cart data, showing default rates");
          this.showDefaultShippingRates(country);
        }
      } catch (error) {
        // Error checking cart - show default rates
        console.log("Error checking cart, showing default rates:", error);
        this.showDefaultShippingRates(country);
      }
      
      this.submitButton.classList.remove("loading");
    }
    
    getDefaultZipCode(country) {
      return this.defaultZipCodes[country] || "00000"; // Return default or fallback
    }
    
    async getShippingRates(zip, country, province) {
      try {
        const response = await fetch(
          `${window.theme.routes.cart_url}/async_shipping_rates.json?shipping_address[zip]=${zip}&shipping_address[country]=${country}&shipping_address[province]=${province}`
        );
        
        if (!response.ok) {
          throw new Error('Failed to fetch shipping rates');
        }
        
        const responseText = await response.text();
        
        if (responseText === "null") {
          return this.getShippingRates(zip, country, province);
        }
        
        const jsonResponse = JSON.parse(responseText);
        
        // Check if shipping_rates exists in the response
        if (!jsonResponse || !jsonResponse.shipping_rates) {
          // If we don't have shipping rates, throw an error to use default rates
          throw new Error('No shipping rates returned');
        }
        
        return jsonResponse.shipping_rates;
      } catch (error) {
        console.error('Error fetching shipping rates:', error);
        // Throw the error up to be caught by the calling function
        throw error;
      }
    }
    
    formatShippingRates(shippingRates) {
      let homeDeliveryOptions = [];
      let relayDeliveryOptions = [];
      
      // Check if shippingRates exists and is an array
      if (!shippingRates || !Array.isArray(shippingRates)) {
        // If no valid shipping rates, use default rates
        const countrySelect = this.querySelector(".shipping-estimator-country");
        const country = countrySelect ? countrySelect.value : "France";
        this.showDefaultShippingRates(country);
        return;
      }
      
      const format = window.theme.settings.money_with_currency_format || "${{amount}}";
      const countrySelect = this.querySelector(".shipping-estimator-country");
      const country = countrySelect ? countrySelect.value : "France";
      
      // Sort shipping rates into home delivery or relay delivery
      shippingRates.forEach((shippingRate) => {
        const price = formatMoney(parseFloat(shippingRate.price) * 100, format);
        const name = shippingRate.presentment_name;
        
        // Calculate delivery date (current date + business days)
        const deliveryDays = name.toLowerCase().includes("mondial relay") ? 4 : 3; // 4 days for Mondial Relay, 3 for others
        const formattedDate = this.getEstimatedDeliveryDate(deliveryDays);
        
        const optionHtml = `<li>${name} - <strong>${price}</strong></li>`;
        
        // Categorize based on shipping method name
        if (name.toLowerCase().includes("mondial relay") || name.toLowerCase().includes("point relais")) {
          relayDeliveryOptions.push({html: optionHtml, date: formattedDate});
        } else {
          homeDeliveryOptions.push({html: optionHtml, date: formattedDate});
        }
      });
      
      this.displayFormattedShippingRates(country, homeDeliveryOptions, relayDeliveryOptions);
    }
    
    // New method to show default shipping rates for empty cart
    showDefaultShippingRates(country) {
      // Ensure translations are loaded
      if (Object.keys(this.translations).length === 0) {
        this.translations = this.getTranslations();
      }
      
      const format = window.theme.settings.money_with_currency_format || "${{amount}}";
      let homeDeliveryOptions = [];
      let relayDeliveryOptions = [];
      
      // Get product price dynamically from the DOM
      const productPrice = this.getCurrentProductPrice();
      
      if (country === "France") {
        // Mondial Relay = 6€
        relayDeliveryOptions.push({
          html: `<li>${this.translations.mondialRelayName} - <strong>${formatMoney(600, format)}</strong></li>`,
          date: this.getEstimatedDeliveryDate(4)
        });
        
        // Colissimo: 8€ if product price is less than 40€, 10€ else
        const colissimoPrice = productPrice < 40 ? 800 : 1000;
        homeDeliveryOptions.push({
          html: `<li>${this.translations.colissimoName} - <strong>${formatMoney(colissimoPrice, format)}</strong></li>`,
          date: this.getEstimatedDeliveryDate(3)
        });
      } else if (country === "Bolivia" || country === "Czech Republic" || country === "United Kingdom") {
        // Colissimo: 18€ if product price is less than 60€, 20€ else
        const colissimoPrice = productPrice < 60 ? 1800 : 2000;
        homeDeliveryOptions.push({
          html: `<li>${this.translations.colissimoName} - <strong>${formatMoney(colissimoPrice, format)}</strong></li>`,
          date: this.getEstimatedDeliveryDate(3)
        });
      } else {
        // For all other countries
        // Colissimo: 18€ if product price is less than 60€, 20€ else
        const colissimoPrice = productPrice < 60 ? 1800 : 2000;
        homeDeliveryOptions.push({
          html: `<li>${this.translations.colissimoName} - <strong>${formatMoney(colissimoPrice, format)}</strong></li>`,
          date: this.getEstimatedDeliveryDate(3)
        });
        
        // Mondial Relay: 6€ for product price less than 60€, 8€ else
        const mondialRelayPrice = productPrice < 60 ? 600 : 800;
        relayDeliveryOptions.push({
          html: `<li>${this.translations.mondialRelayName} - <strong>${formatMoney(mondialRelayPrice, format)}</strong></li>`,
          date: this.getEstimatedDeliveryDate(4)
        });
      }
      
      // Display the shipping rates
      this.displayFormattedShippingRates(country, homeDeliveryOptions, relayDeliveryOptions);
    }
    
    // Helper method to get the current product price from the DOM
    getCurrentProductPrice() {
      try {
        // Find the price element in the DOM
        const priceElement = document.querySelector('#price-template--24622486847812__main-product .price .amount');
        
        if (priceElement) {
          // Get the text content (e.g., "20,00 €")
          const priceText = priceElement.textContent.trim();
          
          // Convert to a number by removing non-numeric characters except decimal point
          // Convert comma to dot for proper parsing
          const cleanPrice = priceText.replace(/[^0-9,]/g, '').replace(',', '.');
          
          // Parse as float
          return parseFloat(cleanPrice);
        }
        
        // If element not found or parsing fails, return a default price (higher than thresholds)
        return 70;
      } catch (error) {
        console.error('Error getting product price:', error);
        // Return default price if there's an error
        return 70;
      }
    }
    
    // Helper to get formatted delivery date
    getEstimatedDeliveryDate(businessDays) {
      const deliveryDate = this.addBusinessDays(new Date(), businessDays);
      return deliveryDate.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }).replace(/\//g, '/');
    }
    
    // Display formatted shipping rates
    displayFormattedShippingRates(country, homeDeliveryOptions, relayDeliveryOptions) {
      // Ensure translations are loaded
      if (Object.keys(this.translations).length === 0) {
        this.translations = this.getTranslations();
      }
      
      // Create HTML for both types of delivery options
      let formattedShippingRates = "";
      
      // Add free shipping message for France
      let freeShippingMessage = "";
      if (country === "France") {
        freeShippingMessage = `<p style="color: #2B8C74; font-weight: bold; margin-top: 0; margin-bottom: 10px;">${this.translations.freeShippingMessage}</p>`;
      }
      
      // Generate relay delivery box if options exist
      if (relayDeliveryOptions.length > 0) {
        const relayDate = relayDeliveryOptions[0].date; // Use the date from the first option
        formattedShippingRates += `
          <div class="product--shipping-box product--shipping-box-has-bg" style="--block-image-width: 30px; margin-bottom: 15px;">
            <div class="product--block-heading" style="font-size: 19px; font-weight: 600; line-height: 100%;">${this.translations.pickupPointTitle}</div>
            <p style="opacity: 0.8;">${this.translations.pickupPointSubtitle}</p>
            <p>${this.translations.deliveredOn} ${relayDate}</p>
            <ul style="list-style: disc; padding-left: 20px;">${relayDeliveryOptions.map(option => option.html).join('')}</ul>
          </div>
        `;
      }
      
      // Generate home delivery box if options exist
      if (homeDeliveryOptions.length > 0) {
        const homeDate = homeDeliveryOptions[0].date; // Use the date from the first option
        formattedShippingRates += `
          <div class="product--shipping-box product--shipping-box-has-bg" style="--block-image-width: 30px; margin-bottom: 15px;">
            <div class="product--block-heading" style="font-size: 19px; font-weight: 600; line-height: 100%;">${this.translations.homeDeliveryTitle}</div>
            <p style="opacity: 0.8;">${this.translations.homeDeliverySubtitle}</p>
            <p>${this.translations.deliveredOn} ${homeDate}</p>
            <ul style="list-style: disc; padding-left: 20px;">${homeDeliveryOptions.map(option => option.html).join('')}</ul>
          </div>
        `;
      }
      
      // Create the HTML with the free shipping message and formatted shipping rates
      const html = `
        ${freeShippingMessage}
        ${formattedShippingRates.length > 0 ? formattedShippingRates : `<p>${this.translations.noShippingOptions}</p>`}
      `;
      
      // Clear previous results and insert new HTML
      this.results.innerHTML = "";
      this.results.insertAdjacentHTML("beforeend", html);
    }
    
    // Helper function to add business days (skipping weekends)
    addBusinessDays(date, days) {
      let result = new Date(date);
      let addedDays = 0;
      while (addedDays < days) {
        result.setDate(result.getDate() + 1);
        const dayOfWeek = result.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          // Skip weekend days (0 = Sunday, 6 = Saturday)
          addedDays++;
        }
      }
      return result;
    }
    
    formatError(errors) {
      // Instead of showing error, just show default rates
      const countrySelect = this.querySelector(".shipping-estimator-country");
      const country = countrySelect ? countrySelect.value : "France";
      this.showDefaultShippingRates(country);
    }
    
    setupCountries() {
      if (Shopify && Shopify.CountryProvinceSelector) {
        let formId = this.dataset.formId;
        new Shopify.CountryProvinceSelector(
          `shipping-estimator-country-${formId}`,
          `shipping-estimator-province-${formId}`,
          { hideElement: `shipping-estimator-province-wrapper-${formId}` }
        );
      }
    }
  }
  customElements.define("shipping-estimator", ShippingEstimator);
}